/**
 * The event vocabulary. Pure — these functions build payloads and nothing else.
 *
 * Every event goes to PostHog once, from whichever side fires it, carrying
 * `product: preview_rubric` and `event_source`. No event carries the writing or the rubric
 * text — counts and categories only.
 *
 * The wire name is `preview_<name>` (see `posthogEventName`), the same prefix objectives
 * uses, so `preview_rubric_scored` is what a reader queries. Renaming any of these is a
 * breaking change for every reader; grep first.
 */

export type EventName =
  | 'rubric_saved'
  | 'rubric_pasted'
  | 'rubric_page_viewed'
  | 'rubric_sample_loaded'
  | 'rubric_library_loaded'
  | 'rubric_fullscreen_toggled'
  | 'rubric_scored'
  | 'rubric_score_failed'
  | 'rubric_item_selected'
  | 'rubric_access_denied';

export interface AnalyticsEvent {
  name: EventName;
  properties: Record<string, string | number | boolean>;
}

export const rubricPageViewed = (): AnalyticsEvent => ({ name: 'rubric_page_viewed', properties: {} });

export const rubricSampleLoaded = (exampleId: string, rubricId: string): AnalyticsEvent => ({
  name: 'rubric_sample_loaded',
  properties: { example_id: exampleId, rubric_id: rubricId },
});

/** A rubric picked from the library on its own, without an example. */
export const rubricLibraryLoaded = (rubricId: string): AnalyticsEvent => ({
  name: 'rubric_library_loaded',
  properties: { rubric_id: rubricId },
});

export const rubricFullscreenToggled = (fullscreen: boolean): AnalyticsEvent => ({
  name: 'rubric_fullscreen_toggled',
  properties: { fullscreen },
});

export const rubricScored = (facts: {
  itemCount: number;
  sentenceCount: number;
  provider: string;
  passedCount: number;
  durationMs?: number;
}): AnalyticsEvent => ({
  name: 'rubric_scored',
  properties: {
    item_count: facts.itemCount,
    sentence_count: facts.sentenceCount,
    provider: facts.provider,
    passed_count: facts.passedCount,
    ...(facts.durationMs !== undefined ? { duration_ms: facts.durationMs } : {}),
  },
});

export const rubricScoreFailed = (reason: string, facts: { itemCount?: number; sentenceCount?: number } = {}): AnalyticsEvent => ({
  name: 'rubric_score_failed',
  properties: {
    reason,
    ...(facts.itemCount !== undefined ? { item_count: facts.itemCount } : {}),
    ...(facts.sentenceCount !== undefined ? { sentence_count: facts.sentenceCount } : {}),
  },
});

export const rubricItemSelected = (itemIndex: number, evidenceCount: number, selected: boolean): AnalyticsEvent => ({
  name: 'rubric_item_selected',
  properties: { item_index: itemIndex, evidence_count: evidenceCount, selected },
});

/** A category, never an address. */
export const rubricAccessDenied = (reason: string): AnalyticsEvent => ({
  name: 'rubric_access_denied',
  properties: { reason },
});

export const posthogEventName = (name: EventName): string =>
  name.startsWith('preview_') ? name : `preview_${name}`;

export const rubricSaved = (itemCount: number, levelCount: number): AnalyticsEvent => ({
  name: 'rubric_saved', properties: { item_count: itemCount, level_count: levelCount },
});

/** A teacher pasted their own rubric table. Shape only, never the text. */
export const rubricPasted = (facts: { source: string; ok: boolean; reason?: string; itemCount?: number; levelCount?: number }): AnalyticsEvent => ({
  name: 'rubric_pasted',
  properties: {
    source: facts.source,
    ok: facts.ok,
    ...(facts.reason !== undefined ? { reason: facts.reason } : {}),
    ...(facts.itemCount !== undefined ? { item_count: facts.itemCount } : {}),
    ...(facts.levelCount !== undefined ? { level_count: facts.levelCount } : {}),
  },
});
